'use client'

import { motion } from 'framer-motion'
import { Check, ArrowRight } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface PricingTier {
  name: string
  price: string
  period: string
  description: string
  features: string[]
  cta: string
  href: string
  popular?: boolean
}

interface PricingTableProps {
  title: string
  description?: string
  tiers: PricingTier[]
  note?: string
  variant?: 'primary' | 'ai'
}

export function PricingTable({
  title,
  description,
  tiers,
  note,
  variant = 'primary',
}: PricingTableProps) {
  const isAi = variant === 'ai'

  return (
    <Section background={isAi ? 'dark' : 'gray'} id="pricing">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className={`text-display-1 mb-4 ${isAi ? 'text-white' : 'text-stone-900'}`}>{title}</h2>
          {description && (
            <p className={`text-body-lg max-w-2xl mx-auto ${isAi ? 'text-stone-300' : ''}`}>
              {description}
            </p>
          )}
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative"
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <Badge variant={isAi ? 'primary' : 'primary'}>
                    Most Popular
                  </Badge>
                </div>
              )}
              <Card
                className={`h-full ${
                  isAi
                    ? tier.popular
                      ? 'bg-stone-900 border-2 border-primary-500 shadow-lg'
                      : 'bg-stone-900 border-stone-800'
                    : tier.popular
                      ? 'border-2 border-primary-500 shadow-lg'
                      : ''
                }`}
              >
                <CardContent className="p-6">
                  <div className="mb-6">
                    <h3 className={`font-heading font-semibold text-lg mb-2 ${
                      isAi ? 'text-white' : 'text-stone-900'
                    }`}>
                      {tier.name}
                    </h3>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className={`text-4xl font-bold ${
                        isAi ? 'text-white' : 'text-stone-900'
                      }`}>
                        {tier.price}
                      </span>
                      {tier.period && (
                        <span className={isAi ? 'text-stone-400' : 'text-stone-500'}>
                          {tier.period}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${isAi ? 'text-stone-400' : 'text-stone-500'}`}>
                      {tier.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check
                          className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                            isAi ? 'text-green-400' : 'text-primary-600'
                          }`}
                        />
                        <span className={`text-sm ${isAi ? 'text-stone-300' : 'text-stone-600'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    href={tier.href}
                    external={tier.href.startsWith('http')}
                    variant={
                      tier.popular
                        ? 'primary'
                        : isAi
                          ? 'dark'
                          : 'secondary'
                    }
                    className="w-full"
                  >
                    {tier.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {note && (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className={`text-center text-sm mt-8 ${isAi ? 'text-stone-400' : 'text-stone-500'}`}
          >
            {note}
          </motion.p>
        )}
      </Container>
    </Section>
  )
}
